import React, { MouseEventHandler, ReactNode, useEffect } from 'react';
import {
    getChatSessionDetails,
    getSessionContent,
} from 'flow/actions/chat-actions';
import { fetchCloudFiles } from 'flow/actions/social-integration-actions';
import BackButton from 'flow/components/ui-components/BackButton';
import BaseButton from 'flow/components/ui-components/BaseButton';
import Icon from 'flow/components/ui-components/Icon';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectExtensionsCapabilitiesAreLoaded,
    selectHasCapability,
    selectOwnCapabilitiesAreLoaded,
    selectOwnExtensionKey,
    selectOwnUserHasCapability,
} from 'flow/selectors/ExtensionSelectors';
import { twMerge } from 'tailwind-merge';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { Capabilities } from 'flow/utils/capability-utils';
import { isDirectMessageSession } from 'flow/utils/message-utils-TW';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    header: string;
    /** The chat this page is showing. Taken as a prop rather than from the
     *  router: the meeting panel renders over whatever page is behind it, so
     *  the route's id is some other chat, or none. */
    chatSessionKey: ChatSessionKey;
    onClickBack?: MouseEventHandler;
    onClickExit?: MouseEventHandler;
    footer?: ReactNode;
    children: ReactNode;
    endButton?: ReactNode;
    /** Default 'center', matching every other MessagesPage consumer. 'start'
     *  left-aligns the title next to onClickBack/onClickExit instead — used
     *  by the meeting chat panel, whose title is a room name rather than a
     *  generic page heading. */
    headerAlign?: 'center' | 'start';
    /** Dark variant, used only by the meeting chat panel; other consumers stay light. */
    videoDark?: boolean;
}

const MessagesPage = ({
    header,
    chatSessionKey,
    onClickBack,
    onClickExit,
    footer,
    children,
    endButton,
    headerAlign = 'center',
    videoDark = false,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const ownUserHasCapabilityChat = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.CHAT),
    );
    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', chatSessionKey]),
    );
    const ownCapabilitiesAreLoaded = useAppSelector(
        selectOwnCapabilitiesAreLoaded,
    );
    const extensionsCapabilitiesAreLoaded = useAppSelector(
        selectExtensionsCapabilitiesAreLoaded,
    );

    /* Taken from the session rather than from a prop: the page above holds the
     * counterpart's key in state that an effect fills in, so it is still null
     * on the first render of a navigation, and a composer that is right one
     * frame later has already flickered. */
    const counterpart = session
        ?.get('members')
        ?.find((m) => m.get('extensionKey') !== ownExtensionKey);

    const counterpartExtensionKey = counterpart?.get('extensionKey');

    const extensionHasCapabilityChat = useAppSelector((state) =>
        selectHasCapability(state, counterpartExtensionKey, Capabilities.CHAT),
    );

    useEffect(() => {
        dispatch(getChatSessionDetails(chatSessionKey));
        dispatch(fetchCloudFiles(chatSessionKey));
        dispatch(getSessionContent(chatSessionKey, 'images'));
    }, [dispatch, chatSessionKey]);

    const isDirectMessage = isDirectMessageSession(session);

    const shouldShowMessageInput = isDirectMessage
        ? ownUserHasCapabilityChat && extensionHasCapabilityChat
        : ownUserHasCapabilityChat;

    /* Whether everything shouldShowMessageInput reads has actually arrived. It
     * matters because a chat session loads before the capabilities of the
     * person it is with, so on the way there the answer swings true, false,
     * then true again: keeping the composer until the answer is settled is
     * what stops it appearing, vanishing and coming back. Once settled, a no
     * means the composer is not rendered at all, so a chat you cannot write in
     * has no empty strip along the bottom. */
    const chatPermissionIsResolved =
        !!session &&
        ownCapabilitiesAreLoaded &&
        (!isDirectMessage ||
            // Nobody to ask about: a member removed from the customer is left
            // with neither an extension nor a contact, so no fetch will ever
            // say more than this already does.
            !counterpartExtensionKey ||
            extensionsCapabilitiesAreLoaded);

    return (
        <div
            className={twMerge(
                'badge-bg @container/messages flex size-full flex-col overflow-hidden',
                videoDark ? 'bg-gray-800 text-white' : 'bg-white',
            )}
        >
            <header
                className={twMerge(
                    'flex h-16 w-full items-center justify-center border-b px-4 font-bold',
                    videoDark ? 'border-gray-700' : '',
                )}
            >
                <div className="flex flex-1 items-center justify-start gap-x-2">
                    {onClickBack ? (
                        <BackButton
                            onClick={onClickBack}
                            className={videoDark ? 'text-white' : ''}
                        />
                    ) : null}

                    {onClickExit ? (
                        <BaseButton
                            aria-label={i18next.t('modal.close')}
                            onClick={onClickExit}
                            className={twMerge(
                                'hover:darken-20',
                                videoDark ? 'text-white' : '',
                            )}
                        >
                            <Icon iconKey="close" />
                        </BaseButton>
                    ) : null}

                    {headerAlign === 'start' ? (
                        <div className="min-w-0 truncate">{header}</div>
                    ) : null}
                </div>

                {headerAlign === 'center' ? header : null}

                <div className="flex flex-1 justify-end">{endButton}</div>
            </header>

            {children}

            {footer && (shouldShowMessageInput || !chatPermissionIsResolved) ? (
                <footer
                    className={twMerge(
                        'flex flex-col items-center px-4 pb-3',
                        videoDark ? 'bg-gray-800' : 'bg-white',
                    )}
                >
                    {footer}
                </footer>
            ) : null}
        </div>
    );
};

export default MessagesPage;
